import apiClient from '../client';
import { API_ENDPOINTS } from '../endpoints';
import type {
  ProjectsResponse,
  ProjectResponse,
  CompanyInfoResponse,
  PaginationParams,
  ContactMessageRequest,
  HomepageContentResponse,
  AboutContentResponse,
  HowItWorksContentResponse,
  FAQsResponse,
  ContactContentResponse,
  NavigationLinksResponse,
  FooterContentResponse,
  SpecializationsResponse,
  ApiResponse,
  SiteSettings,
} from '../types';

export const publicService = {
  getProjects: async (params?: PaginationParams): Promise<ProjectsResponse> => {
    // Default sort: newest first by created_at
    const paramsWithSort = {
      ...params,
      sort_by: params?.sort_by || 'created_at',
      sort_order: params?.sort_order || 'desc'
    };
    
    const response = await apiClient.get<ProjectsResponse>(
      API_ENDPOINTS.PUBLIC.PROJECTS,
      { params: paramsWithSort }
    );
    return response.data;
  },

  getProjectDetails: async (id: number): Promise<ProjectResponse> => {
    const response = await apiClient.get<ProjectResponse>(
      API_ENDPOINTS.PUBLIC.PROJECT_DETAIL(id)
    );
    return response.data;
  },

  getCompanyInfo: async (): Promise<CompanyInfoResponse> => {
    const response = await apiClient.get<CompanyInfoResponse>(
      API_ENDPOINTS.PUBLIC.COMPANY
    );
    return response.data;
  },

  sendContactMessage: async (
    data: ContactMessageRequest
  ): Promise<{ success: boolean; message: string }> => {
    const response = await apiClient.post(API_ENDPOINTS.PUBLIC.CONTACT, data);
    return response.data;
  },

  // Public Content APIs
  getHomepageContent: async (): Promise<HomepageContentResponse> => {
    const response = await apiClient.get<HomepageContentResponse>(
      API_ENDPOINTS.PUBLIC.CONTENT.HOMEPAGE
    );
    return response.data;
  },

  getAboutContent: async (): Promise<AboutContentResponse> => {
    const response = await apiClient.get<AboutContentResponse>(
      API_ENDPOINTS.PUBLIC.CONTENT.ABOUT
    );
    return response.data;
  },

  getHowItWorksContent: async (): Promise<HowItWorksContentResponse> => {
    const response = await apiClient.get<HowItWorksContentResponse>(
      API_ENDPOINTS.PUBLIC.CONTENT.HOW_IT_WORKS
    );
    return response.data;
  },

  getFAQs: async (): Promise<FAQsResponse> => {
    const response = await apiClient.get<FAQsResponse>(
      API_ENDPOINTS.PUBLIC.CONTENT.FAQS
    );
    return response.data;
  },

  getContactContent: async (): Promise<ContactContentResponse> => {
    const response = await apiClient.get<ContactContentResponse>(
      API_ENDPOINTS.PUBLIC.CONTENT.CONTACT
    );
    return response.data;
  },

  getNavigationLinks: async (): Promise<NavigationLinksResponse> => {
    const response = await apiClient.get<NavigationLinksResponse>(
      API_ENDPOINTS.PUBLIC.CONTENT.NAVIGATION
    );
    return response.data;
  },

  getFooterContent: async (): Promise<FooterContentResponse> => {
    const response = await apiClient.get<FooterContentResponse>(
      API_ENDPOINTS.PUBLIC.CONTENT.FOOTER
    );
    return response.data;
  },

  getSpecializations: async (): Promise<SpecializationsResponse> => {
    const response = await apiClient.get<SpecializationsResponse>(
      API_ENDPOINTS.PUBLIC.SPECIALIZATIONS
    );
    return response.data;
  },

  getSiteSettings: async (): Promise<ApiResponse<SiteSettings>> => {
    const response = await apiClient.get<ApiResponse<SiteSettings>>(
      API_ENDPOINTS.PUBLIC.SITE_SETTINGS
    );
    return response.data;
  },
};
